import { OidcService } from '../../types/oidc-service';
import Claims from '../../types/claims';
import { BaseOidcService } from '../base-oidc/base-oidc-service';

type TokenFlowAuthData = {
    accessToken?: string;
    claims?: Claims;
}

export class TokenOidcService extends BaseOidcService implements OidcService {
    private tokenParamName = "crossAppToken";
    private authDataKey = "token.flow.auth.data";

    public init = async (): Promise<void> => {
        const url = this.oidcMetadataConfig!.userinfo_endpoint;
        const crossAppToken = this.getCrossAppUrlToken();

        if(crossAppToken) {
            const accessToken = await this.exchangeToken(crossAppToken);

            if(accessToken) {
                const claims = await this.fetchUserInfo(url, accessToken);

                this.storeAccessToken(accessToken);
                this.storeClaims(claims);
                this.removeCrossAppTokenFromUrl();
            } else {
                return Promise.reject();
            } 
        }
    }

    public isActiveFlow = () => {
        const crossAppToken = this.getCrossAppUrlToken();
        return crossAppToken != null || this.isAuthenticated();
    }

    public isAuthenticated = (): boolean => {
        const authData = this.getAuthData();
        return authData?.claims != null;
    };

    public checkSession = async (): Promise<boolean> => {
        // we already checked session in init method
        // maybe we still need to perform request and handle double checksession logic elsewhere
        return Promise.resolve(true);
    };

    public getAccessToken = async (): Promise<string | null> => {
        const authData = this.getAuthData();
        return Promise.resolve(authData?.accessToken ?? null);
    };

    public getClaims = async (): Promise<Claims> => {
        const authData = this.getAuthData();
        return authData?.claims ? Promise.resolve(authData?.claims) : Promise.reject("No claims found");
    };

    public signinRedirect = async (): Promise<void> => {
        this.clearAuthData();
        await this.userManager?.clearStaleState();
        await this.userManager?.signinRedirect(
            { state: window.location.pathname + window.location.search }
        );
    };

    public signinRedirectCallback = (): Promise<void> => {
        // token flow has no signin redirect callback
        throw new Error("The method is not implemented.");
    };

    public logout = async (): Promise<void> => {
        this.clearAuthData();
        this.userManager?.clearStaleState();
        this.userManager?.signoutRedirect();
        if(this.logoutHandler) {
            this.logoutHandler();
        }
    };

    public onLogout(handler: () => void) {
        this.logoutHandler = handler;
    }

    private storeAccessToken = (accessToken: string) => {
        const authData = this.getAuthData();

        if(authData != null) {
            authData.accessToken = accessToken;
            this.setAuthData(authData);
        } else {
            this.setAuthData({ accessToken: accessToken });
        }
    }

    private storeClaims = (claims: Claims) => {
        const authData = this.getAuthData();

        if(authData != null) {
            authData.claims = claims;
            this.setAuthData(authData);
        } else {
            this.setAuthData({ claims: claims });
        }
    }

    private getAuthData = (): TokenFlowAuthData | null => {
        const authData = localStorage.getItem(this.authDataKey);
        return authData != null ? JSON.parse(authData) : null;
    }

    private setAuthData = (authData: TokenFlowAuthData) => {
        localStorage.setItem(this.authDataKey, JSON.stringify(authData));
    }

    private clearAuthData = () => {
        localStorage.removeItem(this.authDataKey);
    }

    private getCrossAppUrlToken = () => {
        const url = new URL(window.location.href);
        return url.searchParams.get(this.tokenParamName);
    }

    private removeCrossAppTokenFromUrl = () => {
        const token = this.getCrossAppUrlToken();

        if(token != null) {
            const url = new URL(window.location.href);
            url.searchParams.delete(this.tokenParamName)
            window.history.replaceState(null, '', url.toString());
        }
    }

    private exchangeToken = async (crossAppToken: string): Promise<string | null> => {
        const response = await fetch(this.oidcMetadataConfig!.token_endpoint, { 
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            body: `client_id=${this.oidcConfig?.client_id}&token=${crossAppToken}&grant_type=cross_app`
        });

        if(response.ok) {
            return (await response.json())?.access_token as string;
        } else {
            return null;
        }
    }
}
